import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { API, useAuth } from "../App";
import QMSLayout from "../components/QMSLayout";
import axios from "axios";
import {
  Siren, Loader2, RefreshCw, Plus, Shield, AlertTriangle,
  CheckCircle2, XCircle, ChevronRight, Clock, Target,
  Zap, Eye, Settings, Play, Bell, BellOff, Trash2,
  ChevronDown, ChevronUp
} from "lucide-react";

const ax = axios.create({ withCredentials: true });

const SEVERITY_COLORS = {
  critical: { bg: "bg-red-400/10", text: "text-red-400", border: "border-red-400/20" },
  high: { bg: "bg-orange-400/10", text: "text-orange-400", border: "border-orange-400/20" },
  medium: { bg: "bg-amber-400/10", text: "text-amber-400", border: "border-amber-400/20" },
  low: { bg: "bg-emerald-400/10", text: "text-emerald-400", border: "border-emerald-400/20" },
};

const STATUS_BADGES = {
  open: { bg: "bg-red-400/10", text: "text-red-400", label: "Open" },
  acknowledged: { bg: "bg-amber-400/10", text: "text-amber-400", label: "Acknowledged" },
  dismissed: { bg: "bg-[#8B95A5]/10", text: "text-[#8B95A5]", label: "Dismissed" },
};

const RULE_ICONS = {
  repeat_supplier_findings: Target,
  major_critical_recurrence: AlertTriangle,
  overdue_supplier_capa: Clock,
  evidence_gaps: Eye,
  vendor_risk_combined: Shield,
};

/* ═════════════════════════════════════════════════════════════════ */
const SCARescalationPage = () => {
  const { t } = useTranslation('scar');
  const { user } = useAuth();
  const [tab, setTab] = useState("events");
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [evaluating, setEvaluating] = useState(false);
  const [showNewRule, setShowNewRule] = useState(false);
  const [expandedEvent, setExpandedEvent] = useState(null);

  // New rule form
  const [newRule, setNewRule] = useState({
    rule_type: "repeat_supplier_findings",
    name: "", description: "", threshold: 3, severity: "high", enabled: true,
    params: {},
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [s, e, r] = await Promise.all([
        ax.get(`${API}/scar-escalation/stats`).then(r => r.data).catch(() => null),
        ax.get(`${API}/scar-escalation/events?limit=50`).then(r => r.data).catch(() => ({ events: [] })),
        ax.get(`${API}/scar-escalation/rules`).then(r => r.data).catch(() => ({ rules: [] })),
      ]);
      setStats(s); setEvents(e.events || []); setRules(r.rules || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const runEvaluation = async () => {
    setEvaluating(true);
    try {
      const res = await ax.post(`${API}/scar-escalation/evaluate`);
      const data = res.data;
      alert(`Evaluation complete: ${data.total_triggered} event(s) triggered from ${data.rules_checked} rules.`);
      loadData();
    } catch (e) {
      alert("Evaluation failed: " + (e.response?.data?.detail || e.message));
    }
    setEvaluating(false);
  };

  const acknowledgeEvent = async (eventId) => {
    try {
      await ax.post(`${API}/scar-escalation/events/${eventId}/acknowledge`, { notes: "Acknowledged from dashboard" });
      loadData();
    } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); }
  };

  const dismissEvent = async (eventId) => {
    const reason = prompt("Dismissal reason:");
    if (!reason) return;
    try {
      await ax.post(`${API}/scar-escalation/events/${eventId}/dismiss`, { reason });
      loadData();
    } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); }
  };

  const createRule = async () => {
    try {
      await ax.post(`${API}/scar-escalation/rules`, newRule);
      setShowNewRule(false);
      setNewRule({ rule_type: "repeat_supplier_findings", name: "", description: "", threshold: 3, severity: "high", enabled: true, params: {} });
      loadData();
    } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); }
  };

  const toggleRule = async (ruleId, currentEnabled) => {
    try {
      await ax.put(`${API}/scar-escalation/rules/${ruleId}`, { enabled: !currentEnabled });
      loadData();
    } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); }
  };

  const deleteRule = async (ruleId) => {
    if (!window.confirm(t('escalation.deleteConfirm'))) return;
    try {
      await ax.delete(`${API}/scar-escalation/rules/${ruleId}`);
      loadData();
    } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); }
  };

  if (loading) {
    return (
      <QMSLayout title={t('escalation.title')}>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-[#36A9E1] animate-spin" />
        </div>
      </QMSLayout>
    );
  }

  const tabs = [
    { id: "events", label: t('escalation.events'), icon: Bell },
    { id: "rules", label: t('escalation.rules'), icon: Settings },
  ];

  const isAdmin = user?.role === "admin";
  const isAdminOrAuditor = user?.role === "admin" || user?.role === "auditor";

  return (
    <QMSLayout title={t('scar:escalation.title')}>
      <div className="space-y-6" data-testid="scar-escalation-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              <Siren className="w-6 h-6 text-red-400" />
              SCAR Escalation Framework
            </h1>
            <p className="text-sm text-[#8B95A5] mt-1">Deterministic rule engine for automated supplier escalation</p>
          </div>
          <div className="flex items-center gap-2">
            {isAdminOrAuditor && (
              <button
                onClick={runEvaluation}
                disabled={evaluating}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 transition-colors text-sm disabled:opacity-50"
                data-testid="evaluate-rules-btn"
              >
                {evaluating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                {evaluating ? t('escalation.evaluating') : t('escalation.evaluate')}
              </button>
            )}
            <button
              onClick={loadData}
              className="p-2 rounded-lg bg-[#181B23] text-[#8B95A5] hover:text-[#36A9E1] transition-colors"
              data-testid="refresh-escalation"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Stats Strip */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="escalation-stats">
            <StatCard label={t('escalation.stats.totalRules')} value={stats.total_rules} sub={`${stats.enabled_rules} ${t('escalation.stats.enabled')}`} color="text-[#36A9E1]" />
            <StatCard label={t('escalation.stats.openEvents')} value={stats.open_events} color="text-red-400" />
            <StatCard label={t('escalation.stats.acknowledged')} value={stats.acknowledged_events} color="text-amber-400" />
            <StatCard label={t('escalation.stats.dismissed')} value={stats.dismissed_events} color="text-[#8B95A5]" />
            <StatCard label={t('escalation.stats.totalEvents')} value={stats.total_events} color="text-white" />
          </div>
        )}

        {/* Tab Bar */}
        <div className="flex gap-1 bg-[#181B23] rounded-lg p-1" data-testid="escalation-tabs">
          {tabs.map(tabItem => {
            const Icon = tabItem.icon;
            const active = tab === tabItem.id;
            return (
              <button
                key={tabItem.id}
                onClick={() => setTab(tabItem.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded text-sm transition-colors ${
                  active ? "bg-[#36A9E1]/10 text-[#36A9E1]" : "text-[#8B95A5] hover:text-white hover:bg-[#1F222D]"
                }`}
                data-testid={`tab-${tabItem.id}`}
              >
                <Icon className="w-4 h-4" />
                {tabItem.label}
                {tabItem.id === "events" && stats?.open_events > 0 && (
                  <span className="bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 ml-1">{stats.open_events}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* ═══ EVENTS TAB ═══ */}
        {tab === "events" && (
          <div className="space-y-3" data-testid="events-list">
            {events.length === 0 ? (
              <div className="bg-[#1F222D] border border-[#2A2D38] rounded-lg p-8 text-center">
                <Bell className="w-10 h-10 text-[#8B95A5] mx-auto mb-3" />
                <p className="text-[#8B95A5]">No escalation events yet. Run an evaluation to check rules.</p>
              </div>
            ) : (
              events.map(ev => {
                const sev = SEVERITY_COLORS[ev.severity] || SEVERITY_COLORS.medium;
                const st = STATUS_BADGES[ev.status] || STATUS_BADGES.open;
                const Icon = RULE_ICONS[ev.rule_type] || AlertTriangle;
                const expanded = expandedEvent === ev.event_id;
                return (
                  <div
                    key={ev.event_id}
                    className={`bg-[#1F222D] border ${sev.border} rounded-lg overflow-hidden`}
                    data-testid={`event-${ev.event_id}`}
                  >
                    <div
                      className="flex items-center gap-3 p-4 cursor-pointer hover:bg-[#181B23]/50 transition-colors"
                      onClick={() => setExpandedEvent(expanded ? null : ev.event_id)}
                    >
                      <Icon className={`w-5 h-5 ${sev.text} flex-shrink-0`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white text-sm font-medium">{ev.target_name}</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${sev.bg} ${sev.text}`}>{ev.severity}</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${st.bg} ${st.text}`}>{st.label}</span>
                        </div>
                        <p className="text-xs text-[#8B95A5] mt-1 truncate">{ev.details}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-xs text-[#8B95A5]">{ev.rule_name}</span>
                        {expanded ? <ChevronUp className="w-4 h-4 text-[#8B95A5]" /> : <ChevronDown className="w-4 h-4 text-[#8B95A5]" />}
                      </div>
                    </div>
                    {expanded && (
                      <div className="border-t border-[#2A2D38] p-4 bg-[#181B23]/50">
                        <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                          <div><span className="text-[#8B95A5]">Event ID:</span> <span className="text-white font-mono text-xs">{ev.event_id}</span></div>
                          <div><span className="text-[#8B95A5]">Rule:</span> <span className="text-white">{ev.rule_name}</span></div>
                          <div><span className="text-[#8B95A5]">Triggered:</span> <span className="text-white">{new Date(ev.triggered_at).toLocaleString()}</span></div>
                          <div><span className="text-[#8B95A5]">Target:</span> <span className="text-white">{ev.target_type} / {ev.target_id}</span></div>
                        </div>
                        {ev.data && Object.keys(ev.data).length > 0 && (
                          <div className="bg-[#1F222D] rounded p-3 mb-3">
                            <div className="text-xs text-[#8B95A5] mb-1">Data</div>
                            <pre className="text-xs text-white font-mono overflow-x-auto">{JSON.stringify(ev.data, null, 2)}</pre>
                          </div>
                        )}
                        {ev.status === "open" && isAdminOrAuditor && (
                          <div className="flex gap-2">
                            <button
                              onClick={(e) => { e.stopPropagation(); acknowledgeEvent(ev.event_id); }}
                              className="flex items-center gap-1 px-3 py-1.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 text-xs transition-colors"
                              data-testid={`ack-${ev.event_id}`}
                            >
                              <CheckCircle2 className="w-3 h-3" /> Acknowledge
                            </button>
                            {isAdmin && (
                              <button
                                onClick={(e) => { e.stopPropagation(); dismissEvent(ev.event_id); }}
                                className="flex items-center gap-1 px-3 py-1.5 rounded bg-[#8B95A5]/10 text-[#8B95A5] border border-[#8B95A5]/20 hover:bg-[#8B95A5]/20 text-xs transition-colors"
                                data-testid={`dismiss-${ev.event_id}`}
                              >
                                <XCircle className="w-3 h-3" /> Dismiss
                              </button>
                            )}
                          </div>
                        )}
                        {ev.acknowledged_by && (
                          <div className="text-xs text-amber-400 mt-2">
                            Acknowledged by {ev.acknowledged_by} at {new Date(ev.acknowledged_at).toLocaleString()}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* ═══ RULES TAB ═══ */}
        {tab === "rules" && (
          <div className="space-y-4" data-testid="rules-list">
            {isAdmin && (
              <div className="flex justify-end">
                <button
                  onClick={() => setShowNewRule(!showNewRule)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#36A9E1]/10 text-[#36A9E1] border border-[#36A9E1]/20 hover:bg-[#36A9E1]/20 transition-colors text-sm"
                  data-testid="new-rule-btn"
                >
                  <Plus className="w-4 h-4" /> New Rule
                </button>
              </div>
            )}

            {/* New Rule Form */}
            {showNewRule && (
              <div className="bg-[#1F222D] border border-[#36A9E1]/20 rounded-lg p-5 space-y-4" data-testid="new-rule-form">
                <h3 className="text-sm font-semibold text-white">Create Escalation Rule</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-[#8B95A5] mb-1">Rule Type</label>
                    <select
                      value={newRule.rule_type}
                      onChange={e => setNewRule({ ...newRule, rule_type: e.target.value })}
                      className="w-full bg-[#181B23] border border-[#2A2D38] rounded px-3 py-2 text-white text-sm"
                      data-testid="rule-type-select"
                    >
                      <option value="repeat_supplier_findings">Repeat Supplier Findings</option>
                      <option value="major_critical_recurrence">Major/Critical Recurrence</option>
                      <option value="overdue_supplier_capa">Overdue Supplier CAPA</option>
                      <option value="evidence_gaps">Evidence Gaps</option>
                      <option value="vendor_risk_combined">Vendor Risk Combined</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-[#8B95A5] mb-1">Severity</label>
                    <select
                      value={newRule.severity}
                      onChange={e => setNewRule({ ...newRule, severity: e.target.value })}
                      className="w-full bg-[#181B23] border border-[#2A2D38] rounded px-3 py-2 text-white text-sm"
                      data-testid="rule-severity-select"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-[#8B95A5] mb-1">Name</label>
                  <input
                    value={newRule.name}
                    onChange={e => setNewRule({ ...newRule, name: e.target.value })}
                    placeholder={t('escalation.ruleName')}
                    className="w-full bg-[#181B23] border border-[#2A2D38] rounded px-3 py-2 text-white text-sm"
                    data-testid="rule-name-input"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[#8B95A5] mb-1">Description</label>
                  <textarea
                    value={newRule.description}
                    onChange={e => setNewRule({ ...newRule, description: e.target.value })}
                    placeholder={t('escalation.describeCondition')}
                    className="w-full bg-[#181B23] border border-[#2A2D38] rounded px-3 py-2 text-white text-sm h-20 resize-none"
                    data-testid="rule-desc-input"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[#8B95A5] mb-1">Threshold</label>
                  <input
                    type="number"
                    value={newRule.threshold}
                    onChange={e => setNewRule({ ...newRule, threshold: parseInt(e.target.value) || 0 })}
                    className="w-32 bg-[#181B23] border border-[#2A2D38] rounded px-3 py-2 text-white text-sm"
                    data-testid="rule-threshold-input"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={createRule}
                    className="px-4 py-2 rounded bg-[#36A9E1] text-white text-sm hover:bg-[#2B8BC2] transition-colors"
                    data-testid="save-rule-btn"
                  >
                    Create Rule
                  </button>
                  <button
                    onClick={() => setShowNewRule(false)}
                    className="px-4 py-2 rounded bg-[#181B23] text-[#8B95A5] text-sm hover:text-white transition-colors"
                    data-testid="cancel-rule-btn"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Rules List */}
            {rules.length === 0 ? (
              <div className="bg-[#1F222D] border border-[#2A2D38] rounded-lg p-8 text-center">
                <Settings className="w-10 h-10 text-[#8B95A5] mx-auto mb-3" />
                <p className="text-[#8B95A5]">No escalation rules configured.</p>
              </div>
            ) : (
              rules.map(rule => {
                const sev = SEVERITY_COLORS[rule.severity] || SEVERITY_COLORS.medium;
                const Icon = RULE_ICONS[rule.rule_type] || AlertTriangle;
                return (
                  <div
                    key={rule.rule_id}
                    className={`bg-[#1F222D] border border-[#2A2D38] rounded-lg p-4 ${!rule.enabled ? "opacity-50" : ""}`}
                    data-testid={`rule-${rule.rule_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className={`w-5 h-5 ${sev.text} flex-shrink-0`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white text-sm font-medium">{rule.name}</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${sev.bg} ${sev.text}`}>{rule.severity}</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${rule.enabled ? "bg-emerald-400/10 text-emerald-400" : "bg-[#8B95A5]/10 text-[#8B95A5]"}`}>
                            {rule.enabled ? t('escalation.enabled') : t('escalation.disabled')}
                          </span>
                        </div>
                        <p className="text-xs text-[#8B95A5] mt-1">{rule.description}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-[#8B95A5]">
                          <span>Type: <span className="text-white">{rule.rule_type.replace(/_/g, " ")}</span></span>
                          <span>Threshold: <span className="text-white">{rule.threshold}</span></span>
                        </div>
                      </div>
                      {isAdmin && (
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            onClick={() => toggleRule(rule.rule_id, rule.enabled)}
                            className={`p-2 rounded ${rule.enabled ? "text-emerald-400 hover:bg-emerald-400/10" : "text-[#8B95A5] hover:bg-[#8B95A5]/10"} transition-colors`}
                            title={rule.enabled ? t('escalation.disable') : t('escalation.enable')}
                            data-testid={`toggle-${rule.rule_id}`}
                          >
                            {rule.enabled ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => deleteRule(rule.rule_id)}
                            className="p-2 rounded text-red-400 hover:bg-red-400/10 transition-colors"
                            title={t('escalation.delete')}
                            data-testid={`delete-${rule.rule_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </QMSLayout>
  );
};

const StatCard = ({ label, value, sub, color }) => (
  <div className="bg-[#1F222D] border border-[#2A2D38] rounded-lg p-4" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className={`text-2xl font-bold ${color}`}>{value ?? 0}</div>
    <div className="text-xs text-[#8B95A5]">{label}</div>
    {sub && <div className="text-xs text-[#8B95A5] mt-0.5">{sub}</div>}
  </div>
);

export default SCARescalationPage;
